import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Home, MapPin, Layers, Calendar, DollarSign, User, Mail, Phone, ArrowRight, CheckCircle, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import SMSConsentCheckbox from '../components/ui/SMSConsentCheckbox';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const InstantEstimator = () => {
  const { contractorId } = useParams();
  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingConfig, setIsLoadingConfig] = useState(true);
  const [error, setError] = useState(null);
  const [config, setConfig] = useState(null);
  const [estimate, setEstimate] = useState(null);
  
  const [formData, setFormData] = useState({
    // Address
    address: '',
    city: '',
    state: '',
    zip_code: '',
    
    // Roof details
    current_roof_type: 'asphalt',
    desired_roof_type: 'asphalt',
    roof_pitch: 'medium',
    roof_stories: 1,
    
    // Project details
    project_timeline: 'flexible',
    project_reason: 'replacement',
    financing_interest: 'not_sure',
    estimated_budget: 'not_sure',
    notes: '',
    
    // Contact
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    sms_consent: false
  });

  useEffect(() => {
    loadContractorConfig();
  }, [contractorId]);

  const loadContractorConfig = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/instant-estimate/contractor/${contractorId}/config`);
      setConfig(response.data);
    } catch (err) {
      setError('Unable to load estimator. Please check the link and try again.');
    } finally {
      setIsLoadingConfig(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/instant-estimate/calculate`, {
        ...formData,
        contractor_id: contractorId
      });
      setEstimate(response.data);
      setStep(4);
    } catch (err) {
      setError(err.response?.data?.detail || 'Unable to calculate estimate. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const canProceed = () => {
    switch (step) {
      case 1:
        return formData.address && formData.city && formData.state && formData.zip_code;
      case 2:
        return formData.current_roof_type && formData.roof_pitch;
      case 3:
        return formData.first_name && formData.last_name && formData.email;
      default:
        return true;
    }
  };

  if (isLoadingConfig) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-orange-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading estimator...</p>
        </div>
      </div>
    );
  }

  if (error && !config) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-orange-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md text-center">
          <div className="text-red-500 text-5xl mb-4">⚠️</div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Unable to Load</h2>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  // Step 4: Show results
  if (step === 4 && estimate) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-orange-50 py-8 px-4">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Your Estimate is Ready!</h1>
            <p className="text-gray-600">{estimate.message}</p>
          </div>

          {/* Estimate Card */}
          <div className="bg-white rounded-xl shadow-lg overflow-hidden mb-6">
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6">
              <p className="text-blue-100 text-sm mb-1">Estimated Price Range</p>
              <div className="flex items-baseline gap-2">
                <span className="text-4xl font-bold">${estimate.estimated_low.toLocaleString()}</span>
                <span className="text-2xl">-</span>
                <span className="text-4xl font-bold">${estimate.estimated_high.toLocaleString()}</span>
              </div>
              <p className="text-blue-100 text-sm mt-2">Final price depends on inspection and material choices</p>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-gray-500 text-sm">Roof Area</p>
                  <p className="text-2xl font-bold text-gray-900">{estimate.roof_area_sqft.toLocaleString()} sq ft</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-gray-500 text-sm">Roof Squares</p>
                  <p className="text-2xl font-bold text-gray-900">{estimate.roof_squares} squares</p>
                </div>
              </div>
              
              <div className="border-t pt-4">
                <div className="flex justify-between py-2">
                  <span className="text-gray-600">Address</span>
                  <span className="font-medium">{estimate.address}</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-gray-600">Roof Type</span>
                  <span className="font-medium">{estimate.roof_type}</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-gray-600">Complexity</span>
                  <span className="font-medium">{estimate.complexity}</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-gray-600">Measurement Confidence</span>
                  <span className="font-medium">{estimate.measurement_confidence}%</span>
                </div>
              </div>
            </div>
          </div>

          {/* Contractor Info */}
          <div className="bg-white rounded-xl shadow-lg p-6 text-center">
            <h3 className="font-bold text-lg text-gray-900 mb-2">{estimate.contractor_name}</h3>
            <p className="text-gray-600 mb-4">will contact you within 24 hours</p>
            {estimate.contractor_phone && (
              <a 
                href={`tel:${estimate.contractor_phone}`}
                className="inline-flex items-center gap-2 bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700"
              >
                <Phone className="w-5 h-5" />
                Call Now: {estimate.contractor_phone}
              </a>
            )}
          </div>

          {/* Disclaimer */}
          <p className="text-center text-gray-500 text-sm mt-6">
            This is an estimate based on satellite measurements. Final pricing requires an on-site inspection.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-orange-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          {config?.logo_url && (
            <img src={config.logo_url} alt={config.company_name} className="h-16 mx-auto mb-4" />
          )}
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Get Your Instant Roof Estimate
          </h1>
          <p className="text-gray-600">
            {config?.company_name} • {config?.branding?.tagline}
          </p>
        </div>

        {/* Progress Steps */}
        <div className="flex justify-center mb-8">
          <div className="flex items-center gap-2">
            {[1, 2, 3].map((s) => (
              <React.Fragment key={s}>
                <div 
                  className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                    s < step ? 'bg-green-500 text-white' :
                    s === step ? 'bg-blue-600 text-white' :
                    'bg-gray-200 text-gray-500'
                  }`}
                >
                  {s < step ? <CheckCircle className="w-5 h-5" /> : s}
                </div>
                {s < 3 && <div className={`w-12 h-1 ${s < step ? 'bg-green-500' : 'bg-gray-200'}`} />}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 mb-6">
            {error}
          </div>
        )}

        {/* Form Card */}
        <div className="bg-white rounded-xl shadow-lg p-6 md:p-8">
          {/* Step 1: Property Address */}
          {step === 1 && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <MapPin className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Property Address</h2>
                  <p className="text-gray-600 text-sm">Where is your roof located?</p>
                </div>
              </div>

              <div>
                <Label htmlFor="address">Street Address *</Label>
                <Input
                  id="address"
                  value={formData.address}
                  onChange={(e) => handleChange('address', e.target.value)}
                  placeholder="123 Main Street"
                  className="mt-1"
                />
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="col-span-2">
                  <Label htmlFor="city">City *</Label>
                  <Input
                    id="city"
                    value={formData.city}
                    onChange={(e) => handleChange('city', e.target.value)}
                    placeholder="Austin"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="state">State *</Label>
                  <Input
                    id="state"
                    value={formData.state}
                    onChange={(e) => handleChange('state', e.target.value)}
                    placeholder="TX"
                    maxLength={2}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="zip">ZIP Code *</Label>
                  <Input
                    id="zip"
                    value={formData.zip_code}
                    onChange={(e) => handleChange('zip_code', e.target.value)}
                    placeholder="78701"
                    className="mt-1"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Roof Details */}
          {step === 2 && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <Home className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Roof Details</h2>
                  <p className="text-gray-600 text-sm">Tell us about your roof</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="current_roof">Current Roof Material</Label>
                  <select
                    id="current_roof"
                    value={formData.current_roof_type}
                    onChange={(e) => handleChange('current_roof_type', e.target.value)}
                    className="w-full mt-1 p-3 border rounded-lg"
                  >
                    {config?.available_materials?.map(m => (
                      <option key={m.value} value={m.value}>{m.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <Label htmlFor="desired_roof">Desired New Roof Material</Label>
                  <select
                    id="desired_roof"
                    value={formData.desired_roof_type}
                    onChange={(e) => handleChange('desired_roof_type', e.target.value)}
                    className="w-full mt-1 p-3 border rounded-lg"
                  >
                    {config?.available_materials?.map(m => (
                      <option key={m.value} value={m.value}>{m.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <Label>Roof Pitch (Steepness)</Label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-2">
                  {config?.pitch_options?.map(p => (
                    <button
                      key={p.value}
                      type="button"
                      onClick={() => handleChange('roof_pitch', p.value)}
                      className={`p-3 rounded-lg border-2 text-left transition-all ${
                        formData.roof_pitch === p.value 
                          ? 'border-blue-500 bg-blue-50' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="font-medium text-sm">{p.label}</div>
                      <div className="text-xs text-gray-500">{p.description}</div>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <Label htmlFor="stories">Number of Stories</Label>
                <select
                  id="stories"
                  value={formData.roof_stories}
                  onChange={(e) => handleChange('roof_stories', parseInt(e.target.value))}
                  className="w-full mt-1 p-3 border rounded-lg"
                >
                  <option value={1}>1 Story</option>
                  <option value={2}>2 Stories</option>
                  <option value={3}>3+ Stories</option>
                </select>
              </div>

              <div>
                <Label htmlFor="timeline">Project Timeline</Label>
                <select
                  id="timeline"
                  value={formData.project_timeline}
                  onChange={(e) => handleChange('project_timeline', e.target.value)}
                  className="w-full mt-1 p-3 border rounded-lg"
                >
                  {config?.timeline_options?.map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <Label htmlFor="financing">Financing Preference</Label>
                <select
                  id="financing"
                  value={formData.financing_interest}
                  onChange={(e) => handleChange('financing_interest', e.target.value)}
                  className="w-full mt-1 p-3 border rounded-lg"
                >
                  {config?.financing_options?.map(f => (
                    <option key={f.value} value={f.value}>{f.label}</option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Step 3: Contact Info */}
          {step === 3 && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <User className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Contact Information</h2>
                  <p className="text-gray-600 text-sm">Where should we send your estimate?</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="first_name">First Name *</Label>
                  <Input
                    id="first_name"
                    value={formData.first_name}
                    onChange={(e) => handleChange('first_name', e.target.value)}
                    placeholder="John"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="last_name">Last Name *</Label>
                  <Input
                    id="last_name"
                    value={formData.last_name}
                    onChange={(e) => handleChange('last_name', e.target.value)}
                    placeholder="Smith"
                    className="mt-1"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="email">Email Address *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  placeholder="john@example.com"
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="phone">Phone Number</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  placeholder="(555) 123-4567"
                  className="mt-1"
                />
              </div>

              {/* SMS Consent */}
              {formData.phone && (
                <SMSConsentCheckbox
                  checked={formData.sms_consent}
                  onChange={(checked) => handleChange('sms_consent', checked)}
                  companyName={config?.company_name || "Triple R Exteriors"}
                  required={false}
                  variant="compact"
                />
              )}

              <div>
                <Label htmlFor="notes">Additional Notes (Optional)</Label>
                <textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => handleChange('notes', e.target.value)}
                  placeholder="Any specific concerns or details about your roof..."
                  className="w-full mt-1 p-3 border rounded-lg h-24 resize-none"
                />
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-8 pt-6 border-t">
            {step > 1 ? (
              <Button
                variant="outline"
                onClick={() => setStep(step - 1)}
              >
                Back
              </Button>
            ) : (
              <div />
            )}

            {step < 3 ? (
              <Button
                onClick={() => setStep(step + 1)}
                disabled={!canProceed()}
                className="bg-blue-600 hover:bg-blue-700"
              >
                Continue
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={!canProceed() || isLoading}
                className="bg-green-600 hover:bg-green-700"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Calculating...
                  </>
                ) : (
                  <>
                    Get My Estimate
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            )}
          </div>
        </div>

        {/* Trust Badge */}
        <p className="text-center text-gray-500 text-sm mt-6">
          🔒 Your information is secure and will only be shared with {config?.company_name}
        </p>
      </div>
    </div>
  );
};

export default InstantEstimator;
